/**
 * Parked chat storage for the embedded eid-chatbot widget.
 * Persists long-running agent conversations so users can switch away and return.
 */

import type { ChatMessage, StreamingProgress } from '../types'

const PARKED_CHATS_KEY = 'eid-chatbot-parked-chats'
const MAX_PARKED_CHATS = 10

export interface ParkedThread {
    id: string
    agent: string
    agentName: string
    parked_at: string
    last_progress: StreamingProgress
    conversation_id: string
    run_id?: string
    thread_id: string
    last_user_message: string
    estimated_completion?: number
    status: 'parked' | 'completing' | 'complete' | 'error'
    /** Serialized chat messages at park time */
    messages_snapshot: ChatMessage[]
}

function serializeMessages(messages: ChatMessage[]): ChatMessage[] {
    return messages.map(m => ({
        ...m,
        timestamp: m.timestamp instanceof Date ? m.timestamp : new Date(m.timestamp as unknown as string),
        isStreaming: false,
    }))
}

export function loadParkedChats(): ParkedThread[] {
    try {
        const stored = localStorage.getItem(PARKED_CHATS_KEY)
        if (!stored) return []
        const chats = JSON.parse(stored) as ParkedThread[]
        if (!Array.isArray(chats)) return []

        const now = Date.now()
        const valid = chats.filter(c => {
            const age = now - new Date(c.parked_at).getTime()
            return age < 24 * 60 * 60 * 1000
        })
        if (valid.length !== chats.length) saveParkedChats(valid)
        return valid.map(c => ({
            ...c,
            messages_snapshot: (c.messages_snapshot ?? []).map(m => ({
                ...m,
                timestamp: new Date(m.timestamp as unknown as string),
            })),
        }))
    } catch {
        return []
    }
}

export function saveParkedChats(chats: ParkedThread[]): void {
    try {
        const sorted = [...chats].sort(
            (a, b) => new Date(b.parked_at).getTime() - new Date(a.parked_at).getTime(),
        )
        localStorage.setItem(PARKED_CHATS_KEY, JSON.stringify(sorted.slice(0, MAX_PARKED_CHATS)))
    } catch (err) {
        if (err instanceof DOMException && err.name === 'QuotaExceededError') {
            try {
                localStorage.setItem(PARKED_CHATS_KEY, JSON.stringify(chats.slice(0, 5)))
            } catch {
                /* ignore */
            }
        }
    }
}

export function parkChat(params: {
    threadId: string
    agentName: string
    displayName?: string
    progress: StreamingProgress
    conversationId: string
    runId?: string
    lastUserMessage: string
    messages: ChatMessage[]
}): ParkedThread {
    const parked: ParkedThread = {
        id: `parked-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
        agent: params.agentName,
        agentName: params.displayName || params.agentName,
        parked_at: new Date().toISOString(),
        last_progress: params.progress,
        conversation_id: params.conversationId,
        run_id: params.runId,
        thread_id: params.threadId,
        last_user_message: params.lastUserMessage || 'Chat in progress…',
        estimated_completion: params.progress.estimated_remaining_ms,
        status: 'parked',
        messages_snapshot: serializeMessages(params.messages),
    }
    const chats = loadParkedChats()
    chats.push(parked)
    saveParkedChats(chats)
    return parked
}

export function removeParkedChat(chatId: string): void {
    saveParkedChats(loadParkedChats().filter(c => c.id !== chatId))
}

export function getParkedChatByThreadId(threadId: string): ParkedThread | null {
    return loadParkedChats().find(c => c.thread_id === threadId) ?? null
}

export function updateParkedChat(chatId: string, updates: Partial<ParkedThread>): ParkedThread | null {
    const chats = loadParkedChats()
    const idx = chats.findIndex(c => c.id === chatId)
    if (idx === -1) return null
    chats[idx] = { ...chats[idx], ...updates }
    saveParkedChats(chats)
    return chats[idx]
}
